import {
  Download,
  Eye,
  EyeOff,
  MessageSquare,
  PanelLeftOpen,
} from "lucide-react";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import "../../styles/canvas-object-chrome.css";
import { CHROME_GAP, CHROME_ICON_SEAT, CHROME_T1 } from "./chromeConstants";
import { buildFootRungs, chromeFit } from "./chromeFit";

export type CanvasObjectChromeProps = {
  /** The node's title. Note-only for the pilot; hidden-label kinds pass "". */
  label: string;
  /** Node width in flow px (the chrome derives everything from this + zoom). */
  width: number;
  /** Current canvas zoom. */
  zoom: number;
  /** Hovered or pinned — reveals verbs, eye, and foot. */
  active: boolean;
  /** Authorship foot: display name ("Cohesive" for agent-authored). */
  author?: string;
  /** A short state line for the foot, left of authorship — what the object is
   *  doing right now ("Editing — Esc to finish") or what a gesture would do
   *  ("Click to use · double-click to open"). Wears the foot's own type and
   *  color, so state reads as chrome rather than as a floating control. */
  status?: string;
  /** Human-relative timestamp, e.g. "2h ago"; `whenShort` e.g. "2h". */
  when?: string;
  whenShort?: string;
  /** Downloadable kinds render the download seat in the foot. */
  onDownload?: () => void;
  onQuickChat?: () => void;
  onOpenPane?: () => void;
  /** The eye toggles the label's visibility tier (hover-reveal only). */
  onToggleEye?: () => void;
  /** Hidden label: invisible at rest, revealed while active (hover only). */
  labelHidden?: boolean;
  /** Selected: the gold hover ring clears (selection chrome takes over)
   *  and the chrome freezes — consumers keep `active` true while selected. */
  selected?: boolean;
  /** In-place title editing: click to type, Enter/Escape finish, commit on
   *  blur. The system-wide focus ring marks edit mode. Omit = read-only. */
  onLabelCommit?: (label: string) => void;
};

/** Icon glyphs render 13px at stroke 2 via CSS; lucide's attribute is a
 *  decoy the stylesheet overrides (entity-cards recipe). */
const STROKE = 2;

/**
 * The chrome bands a canvas object wears: label + eye + right-anchored
 * verbs above, authorship + download below. Screen-constant above T2 via
 * counter-scaling; presence ruled by the three-regime machine in
 * `chromeFit`. Render inside a `position: relative` wrapper that matches
 * the node's box — the bands sit in the gutters outside it.
 *
 * Memoized: a node re-renders every frame while dragged (position props),
 * and on a stress canvas the chrome subtree must not re-reconcile then.
 * Callers keep handler props referentially stable (useCallback).
 */
export const CanvasObjectChrome = memo(function CanvasObjectChrome({
  label,
  width,
  zoom,
  active,
  author,
  status,
  when,
  whenShort,
  onDownload,
  onQuickChat,
  onOpenPane,
  onToggleEye,
  labelHidden,
  selected,
  onLabelCommit,
}: CanvasObjectChromeProps) {
  // Chrome interactions never select the node. React's synthetic
  // stopPropagation fires at the React root AFTER the native event has
  // already bubbled through React Flow's node-level native listeners —
  // only a native listener on the element itself stops that bubble.
  const stopNativePointer = useCallback((el: HTMLElement | null) => {
    el?.addEventListener("pointerdown", (e) => e.stopPropagation());
  }, []);
  // Stable composite ref for the label. An inline arrow here would get a new
  // identity every render, making React re-run it (detach/attach) per render
  // — and each run adds ANOTHER native listener: a leak that grows with
  // every re-render of every note.
  const labelRefCallback = useCallback(
    (el: HTMLSpanElement | null) => {
      labelRef.current = el;
      stopNativePointer(el);
    },
    [stopNativePointer],
  );
  // In-place editing. While editing, React must not re-render the text or
  // the caret resets mid-word — the span's content is frozen at focus and
  // read back at blur.
  const [editing, setEditing] = useState(false);
  const labelRef = useRef<HTMLSpanElement>(null);
  const frozenLabel = useRef(label);
  const startEdit = useCallback(() => {
    if (!onLabelCommit) return;
    frozenLabel.current = label;
    setEditing(true);
  }, [onLabelCommit, label]);
  const finishEdit = useCallback(() => {
    if (!labelRef.current) return;
    const next = (labelRef.current.textContent ?? "").trim();
    setEditing(false);
    if (next && next !== label) onLabelCommit?.(next);
    else if (labelRef.current) labelRef.current.textContent = label;
  }, [label, onLabelCommit]);
  const onLabelKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      // Enter never inserts a newline in a single-line title; both keys blur
      // (blur commits). Space must not bubble into node-level handlers.
      if (e.key === "Enter" || e.key === "Escape") {
        e.preventDefault();
        labelRef.current?.blur();
      }
      e.stopPropagation();
    },
    [],
  );
  const footRungs = useMemo(
    () => (author ? buildFootRungs(author, when, whenShort) : [""]),
    [author, when, whenShort],
  );
  // Fixed foot elements beside the text: the download seat + gaps.
  // The foot is name-only by ruling (2026-08-12) — no avatar, no chip.
  const footFixed = onDownload ? CHROME_ICON_SEAT + 2 * CHROME_GAP : 0;
  const verbCount = (onQuickChat ? 1 : 0) + (onOpenPane ? 1 : 0);

  const fit = chromeFit({
    width,
    zoom,
    active,
    verbCount,
    footRungs,
    footFixed,
  });

  const bandStyle = {
    width: fit.bandWidth,
    transform: `scale(${fit.scale})`,
  } as const;
  // The zoom regime decides what is MOUNTED; hover only fades it. The
  // ec reveal recipe: persistent elements, opacity 180ms both ways —
  // a mount/unmount pop reads sudden next to the entity-cards page.
  const mounted = zoom >= CHROME_T1;
  const on = fit.showVerbs ? " is-on" : "";

  return (
    <>
      {/* Hover ring — 1px gold hairline, 2px breathing gap, concentric.
          Ruled: visible both modes (65% light / 32% dark); clears on
          selection. Radius derives from --coc-obj-r on the wrapper. */}
      <div
        className={`coc-ring${active && !selected ? " is-on" : ""}`}
        aria-hidden="true"
      />
      <div className={`coc-head${on}`} style={bandStyle}>
        {label !== "" && (
          <span
            ref={labelRefCallback}
            className={`coc-label${onLabelCommit ? " coc-label--editable nodrag nopan" : ""}${labelHidden ? ` coc-label--hidden${active ? " is-on" : ""}` : ""}`}
            style={{ maxWidth: fit.labelMax }}
            contentEditable={!!onLabelCommit}
            suppressContentEditableWarning
            spellCheck={false}
            onPointerDown={(e) => {
              if (onLabelCommit) e.stopPropagation();
            }}
            // React Flow's selection-on-click is a REACT synthetic handler
            // on its node wrapper — native stops never touch it; the
            // synthetic bubble must be stopped here.
            onClick={(e) => {
              if (onLabelCommit) e.stopPropagation();
            }}
            onFocus={onLabelCommit ? startEdit : undefined}
            onBlur={editing ? finishEdit : undefined}
            onKeyDown={editing ? onLabelKeyDown : undefined}
          >
            {editing ? frozenLabel.current : label}
          </span>
        )}
        {mounted && onToggleEye && (
          <button
            type="button"
            ref={stopNativePointer}
            className={`coc-icon coc-reveal nodrag nopan${on}`}
            aria-label={labelHidden ? "Show label" : "Hide label"}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onToggleEye();
            }}
          >
            {labelHidden ? (
              <EyeOff strokeWidth={STROKE} />
            ) : (
              <Eye strokeWidth={STROKE} />
            )}
          </button>
        )}
        {mounted && verbCount > 0 && (
          <span className={`coc-verbs coc-reveal${on}`}>
            {onQuickChat && (
              <button
                type="button"
                ref={stopNativePointer}
                className="coc-icon nodrag nopan"
                aria-label="Quick chat"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onQuickChat();
                }}
              >
                <MessageSquare strokeWidth={STROKE} />
              </button>
            )}
            {onOpenPane && (
              <button
                type="button"
                ref={stopNativePointer}
                className="coc-icon nodrag nopan"
                aria-label="Open in pane"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenPane();
                }}
              >
                <PanelLeftOpen strokeWidth={STROKE} />
              </button>
            )}
          </span>
        )}
      </div>
      {mounted && (author || status || onDownload) && (
        <div className={`coc-foot coc-reveal${on}`} style={bandStyle}>
          {status && <span className="coc-foot-text">{status}</span>}
          {fit.foot !== "" && author && (
            <span className="coc-foot-text">{fit.foot}</span>
          )}
          {onDownload && (
            <button
              type="button"
              ref={stopNativePointer}
              className="coc-icon coc-download nodrag nopan"
              aria-label="Download"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
            >
              <Download strokeWidth={STROKE} />
            </button>
          )}
        </div>
      )}
    </>
  );
});
